import React from "react";
import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "foreign-exchange-market-exchange-rate-determination-india",
  title: "Foreign Exchange Market: Exchange Rate Systems, PPP Theory, Interest Rate Parity & India's Forex Policy",
  description: "Master the foreign exchange market, exchange rate determination, PPP theory, interest rate parity, and the evolution of India's forex policy and RBI intervention.",
  pubDate: "2025-08-21",
  authorSlug: "dhairya-madaan",
  category: "subject-explainers",
  tags: ["macroeconomics", "international economics", "forex", "exchange rate", "indian economy"],
  readMinutes: 18,
  heroEyebrow: "Macroeconomics Guide"
};

export function Body() {
  return (
    <>
      <p>
        In an increasingly globalized world, the Foreign Exchange (Forex) market is the backbone of international trade and capital flows. For economics students, understanding how exchange rates are determined, the theories behind them, and how central banks manage them is vital. This guide covers exchange rate systems, PPP theory, Interest Rate Parity, and a deep dive into India's forex evolution and RBI policies.
      </p>

      <h2>1. The Foreign Exchange Market Structure</h2>
      <p>
        The Forex market is a decentralized global market where currencies are traded. It is the largest and most liquid financial market in the world.
      </p>
      <ul>
        <li><strong>Spot Rate:</strong> The exchange rate for immediate delivery (usually settled in T+2 days).</li>
        <li><strong>Forward Rate:</strong> The agreed-upon exchange rate for a currency trade that will settle at a specified future date. Used heavily for hedging exchange rate risk.</li>
      </ul>

      <h2>2. Exchange Rate Systems</h2>
      <p>How a country manages its currency relative to others falls into three broad categories:</p>
      <ul>
        <li><strong>Fixed (Pegged) Exchange Rate:</strong> The government or central bank ties the currency's official exchange rate to another country's currency or the price of gold (e.g., Bretton Woods system). The central bank buys/sells forex to maintain the peg.</li>
        <li><strong>Floating Exchange Rate:</strong> The rate is determined entirely by free-market forces of demand and supply without government intervention.</li>
        <li><strong>Managed Float (Dirty Float):</strong> The exchange rate is mostly market-determined, but the central bank occasionally intervenes to smooth out extreme volatility. This is the system India currently follows.</li>
      </ul>

      <h2>3. Determination of Exchange Rate</h2>
      <p>
        In a floating system, the exchange rate is the price of one currency in terms of another. It is determined by the intersection of the demand for and supply of foreign exchange.
      </p>
      <ul>
        <li><strong>Demand for Forex (Outflows):</strong> Importing goods/services, investing abroad, unilateral transfers abroad, Indians travelling abroad. (Downward sloping curve).</li>
        <li><strong>Supply of Forex (Inflows):</strong> Exporting goods/services, Foreign Direct Investment (FDI) and FPI coming in, remittances from NRIs. (Upward sloping curve).</li>
      </ul>
      <p>An increase in exports shifts the supply curve right, leading to <em>appreciation</em> of the domestic currency. An increase in imports shifts demand right, leading to <em>depreciation</em>.</p>

      <h2>4. Core Theories of Exchange Rate Determination</h2>

      <h3>Purchasing Power Parity (PPP) Theory</h3>
      <p>
        Pioneered by Gustav Cassel, PPP states that exchange rates between currencies are in equilibrium when their purchasing power is the same in each of the two countries.
      </p>
      <p><strong>Absolute PPP:</strong> The exchange rate (E) is the ratio of price levels (P) in two countries. E = P_domestic / P_foreign. It relies on the Law of One Price (arbitrage eliminates price differences of identical goods).</p>
      <p><strong>Relative PPP:</strong> States that the rate of change in the exchange rate is equal to the difference in inflation rates between the two countries.
      <br />%ΔE ≈ Inflation_domestic - Inflation_foreign.
      <br />If India has 6% inflation and the US has 2% inflation, the Rupee should depreciate by roughly 4% against the USD.</p>

      <h3>Interest Rate Parity (IRP) Theorem</h3>
      <p>
        Links exchange rates to interest rates. It states that the difference in interest rates between two countries should be equal to the differential between the forward exchange rate and the spot exchange rate.
      </p>
      <p>
        <strong>Formula:</strong> F / S = (1 + i_domestic) / (1 + i_foreign)
        <br />Where F = Forward rate, S = Spot rate.
        <br />If domestic interest rates are higher, the currency will trade at a <em>forward discount</em> to prevent riskless arbitrage.
      </p>

      <h3>Mundell-Fleming Model Link</h3>
      <p>
        Shows that under perfect capital mobility, an independent monetary policy is impossible under fixed exchange rates (The Impossible Trinity). To control interest rates, a country must float its currency.
      </p>

      <h2>5. India's Exchange Rate History & Policy</h2>
      <p>India's forex policy has undergone radical transformations:</p>
      <ul>
        <li><strong>Post-Independence to 1991:</strong> Rupee was pegged first to the Pound Sterling, then to a basket of currencies. Strict forex controls under FERA. Major devaluations occurred in 1966 and 1991 due to balance of payments crises.</li>
        <li><strong>LERMS (1992):</strong> Liberalized Exchange Rate Management System. Introduced a dual exchange rate as a transition step.</li>
        <li><strong>1993 Onwards:</strong> Full convertibility on the current account. India moved to a market-determined <strong>Managed Float</strong>.</li>
      </ul>

      <h3>RBI Intervention and Sterilization</h3>
      <p>
        When massive foreign capital flows in (FDI/FPI), the Rupee appreciates, which can hurt exports. To prevent this, the RBI buys USD from the market, injecting Rupees. This increases domestic money supply, risking inflation.
        <br /><strong>Sterilization:</strong> To neutralize the inflationary impact, the RBI conducts Open Market Operations (OMO), selling government bonds to suck the excess Rupees back out of the system.
      </p>

      <h3>Forex Reserves and REER</h3>
      <p>
        India maintains massive forex reserves (over $600 billion) comprising Foreign Currency Assets, Gold, SDRs, and Reserve Tranche Position in the IMF. It serves as a buffer against external shocks.
      </p>
      <p>
        <strong>REER (Real Effective Exchange Rate):</strong> The weighted average of a country's currency in relation to an index or basket of other major currencies, adjusted for the effects of inflation. A REER &gt; 100 implies the currency is overvalued.
      </p>

      <h2>6. Worked Numerical Problems</h2>

      <h3>Problem 1: Cross Rates</h3>
      <p>
        <strong>Question:</strong> USD/INR = 82.50. GBP/USD = 1.25. Calculate the GBP/INR cross rate.
      </p>
      <p>
        <strong>Solution:</strong>
        <br />GBP/INR = (GBP/USD) * (USD/INR)
        <br />GBP/INR = 1.25 * 82.50 = 103.125.
      </p>

      <h3>Problem 2: Relative PPP</h3>
      <p>
        <strong>Question:</strong> Current USD/INR = 80. Expected inflation in India is 6% and in the US is 2%. What is the expected spot rate one year from now?
      </p>
      <p>
        <strong>Solution:</strong>
        <br />% Depreciation of INR = 6% - 2% = 4%.
        <br />Expected Rate = 80 * (1 + 0.04) = 80 * 1.04 = 83.20.
      </p>

      <h3>Problem 3: Covered Interest Arbitrage & IRP</h3>
      <p>
        <strong>Question:</strong> Spot USD/INR = 80. India Interest Rate = 7%. US Interest Rate = 3%. Find the 1-year forward rate implied by IRP.
      </p>
      <p>
        <strong>Solution:</strong>
        <br />F / S = (1 + i_inr) / (1 + i_usd)
        <br />F / 80 = (1 + 0.07) / (1 + 0.03)
        <br />F / 80 = 1.07 / 1.03 ≈ 1.0388
        <br />F = 80 * 1.0388 = 83.11. (INR is at a forward discount).
      </p>
      
      <p>
        Mastering these theoretical and mathematical components of the forex market provides deep insights into how macroeconomic policies and global trade interconnect, a crucial skill for any modern economist.
      </p>
    </>
  );
}


